/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <section
    id="historySection"
    class="row"
    aria-labelledby="historyLabel"
  >
    <span class="sr-only">
      This page allows you to compare a single score across all your recordings. The results
      are shown in the table below. Change which recordings are compared by checking or
      unchecking recordings on the Recordings tab. Change what score is being used by
      selecting a score from either the Plot Dials tab or the Plot Vibes tab.
    </span>
    <h1
      v-if="pageMode === 'speaker'"
      id="historyLabel"
      class="sr-only"
      style="margin-top:0"
    >
      History
    </h1>
    <div
      v-else
      class="headerCells"
    >
      <label class="field-label">
        MEMBER
      </label>
      <group-user-select
        v-if="!loading"
        :key="userKey"
        :groups="activeGroups"
        :users="groupMembers"
        mode="user"
        :multiple="false"
        @groupUserChange="setUser"
      />
    </div>
    <hr
      v-if="pageMode !== 'speaker'"
      class="coach-hr"
    >
    <div class="col-md-9">
      <history-chart
        v-if="!loading"
        :recordings="selectedRecordings"
        :score="(tab === 'plot vibes') ? plottedVibe : plottedScore"
      />
      <br><br>
    </div>
    <h2
      id="history-nav"
      class="sr-only"
    >
      History Navigation
    </h2>
    <nav
      id="history-navigation"
      class="col-md-3"
      aria-labelledby="history-nav"
    >
      <ul
        id="history-navigation-tabs"
        class="nav nav-tabs"
        aria-label="page navigation"
      >
        <li
          :class="{ active: tab === 'recordings' }"
        >
          <a
            id="recordingslink"
            ref="recordingslink"
            href="#"
            @click.prevent="loadTab('recordings')"
            @keypress="loadTab('recordings', $event)"
          >
            Recordings
          </a>
        </li>
        <li
          :class="{ active: tab === 'plot dials' }"
        >
          <a
            id="plotdialslink"
            ref="plotdialslink"
            href="#"
            @click.prevent="loadTab('plot dials')"
            @keypress="loadTab('plot dials', $event)"
          >
            Plot Dials
          </a>
        </li>
        <li
          :class="{ active: tab === 'plot vibes' }"
        >
          <a
            id="plotvibeslink"
            ref="plotvibeslink"
            href="#"
            @click.prevent="loadTab('plot vibes')"
            @keypress="loadTab('plot vibes', $event)"
          >
            Plot Vibes
          </a>
        </li>
      </ul>
      <div
        v-show="tab === 'recordings'"
        class="recordings-list"
      >
        <h2
          id="recordingsheader"
          class="sr-only"
        >
          Select Recordings Form
        </h2>
        <p
          v-if="recordings && recordings.length"
          class="deselection"
        >
          <a
            tabindex="0"
            @keypress="toggleSelected"
            @click.prevent="toggleSelected()"
          >
            {{ (deselectAll) ? 'Deselect' : 'Select' }} All Recordings
          </a>
        </p>
        <p
          v-if="!recordings || !recordings.length"
        >
          <br>
          No recordings for this user.
          <br><br>
        </p>
        <p
          v-for="recording in recordings"
          :key="recording.recordingId"
        >
          <label
            class="checkbox-inline"
            :for="`check_${recording.recordingId}`"
          >
            <input
              :id="`check_${recording.recordingId}`"
              v-model="selectedIds"
              tabindex="0"
              type="checkbox"
              :name="`check_${recording.recordingId}`"
              :value="recording.recordingId"
            >
            <strong>{{ recording.title }}</strong>
          </label>
        </p>
      </div>
      <div
        v-show="tab === 'plot dials'"
        class="tab-well"
      >
        <h2
          id="plotdialsheader"
          class="sr-only"
        >
          Plot Dials Form
        </h2>
        <p
          v-for="plottableScore in plottableScores"
          class="radio"
        >
          <label
            :for="`plot-${plottableScore.key}`"
            class="plottable-metric"
          >
            <input
              :id="`plot-${plottableScore.key}`"
              v-model="plottedScore"
              tabindex="0"
              type="radio"
              name="plottableScore"
              :value="plottableScore"
            >
            {{ plottableScore.name }}
          </label>
        </p>
      </div>
      <div
        v-show="tab === 'plot vibes'"
        class="tab-well"
      >
        <h2
          id="plotvibesheader"
          class="sr-only"
        >
          Plot Vibes Form
        </h2>
        <div class="row">
          <div class="col-sm-6 positive-vibes">
            <span class="vibe-subhead">
              Positive Vibes
            </span>
            <p
              v-for="plottableVibe in plottableVibes"
              v-if="plottableVibe.vibeType === 'positive'"
              class="radio"
            >
              <label
                :for="`plot-${plottableVibe.key}`"
                class="plottable-metric"
              >
                <input
                  :id="`plot-${plottableVibe.key}`"
                  v-model="plottedVibe"
                  tabindex="0"
                  type="radio"
                  name="plottedVibe"
                  :value="plottableVibe"
                >
                {{ plottableVibe.name }}
              </label>
            </p>
          </div>
          <div class="col-sm-6 negative-vibes">
            <span class="vibe-subhead">
              Negative Vibes
            </span>
            <p
              v-for="plottableVibe in plottableVibes"
              v-if="plottableVibe.vibeType === 'negative'"
              class="radio"
            >
              <label
                :for="`plot-${plottableVibe.key}`"
                class="plottable-metric"
              >
                <input
                  :id="`plot-${plottableVibe.key}`"
                  v-model="plottedVibe"
                  tabindex="0"
                  type="radio"
                  name="plottedVibe"
                  :value="plottableVibe"
                >
                {{ plottableVibe.name }}
              </label>
            </p>
          </div>
        </div>
      </div>
    </nav>
  </section>
</template>

<style lang="less" scoped>
  .plottable-metric, .checkbox-inline {
    font-size: 11px;
  }
  .headerCells {
    width: 370px;
    padding-left: 20px;
  }
  .coach-hr {
    border-bottom:  1px solid #CCCCCC;
  }
  .headerCells label {
    padding-left:  7px;
  }

  #history-navigation-tabs li {
    width: 75px;
  }
  .col-md-3 {
    padding: 0 0 0 40px;
  }
  .col-md-9 {
    padding: 4px 0 0 5px;
  }
  .nav li a {
    padding: 6px 0;
    font-size: 11px;
  }

  .vibe-subhead {
    font-weight: bold;
    font-size: 12pt;
   }

  /* Restore styles overwritten by vue-strap */
  .radio label input, label.checkbox-inline input {
    margin: 2px 0 0 -20px;
    opacity: 1;
  }

  #historySection {
    display: block;
    min-height: 1000px;
  }

  #history-navigation {
    padding: 0px 8px 0 0;

    @media (max-width: 1091px) {
      padding-left: 8px;
    }

    .tab-well, .recordings-list {
      max-height: 450px;
    }
    .tab-well > *, .recordings-list > * {
      padding: 5px;
    }
    .positive-vibes {
      padding-left: 0px;
    }
    .negative-vibes {
      padding-left: 0px;
    }

    p.deselection a {
      text-decoration: underline;
      cursor: pointer;
      color: #F26724;
    }
    @media print {
      #history-navigation {
        display: none;
      }
    }
  }
</style>

<script>
import GroupUserSelect from '../specials/GroupUserSelect';
import HistoryChart from '../specials/HistoryChart';
import BasePage from '../pages/BasePage';

const plottableVibes = [
  { key: 'vibes.assertive', name: 'Assertive', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.authentic', name: 'Authentic', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.captivating', name: 'Captivating', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.clear', name: 'Clear', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.confident', name: 'Confident', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.energetic', name: 'Energetic', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.personable', name: 'Personable', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.organized', name: 'Organized', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.persuasive', name: 'Persuasive', unit: 'score', vibeType: 'positive' },
  { key: 'vibes.arrogant', name: 'Arrogant', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.belligerent', name: 'Belligerent', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.boring', name: 'Boring', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.condescending', name: 'Condescending', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.detached', name: 'Detached', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.ditsy', name: 'Ditsy', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.nervous', name: 'Nervous', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.confusing', name: 'Confusing', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.unapproachable', name: 'Unapproachable', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.pushy', name: 'Pushy', unit: 'score', vibeType: 'negative' },
  { key: 'vibes.timid', name: 'Timid', unit: 'score', vibeType: 'negative' },
];

export default {
  components: { HistoryChart, GroupUserSelect },
  mixins: [ BasePage ],
  data() {
    return {
      tab: 'recordings',
      recordings: [],
      userKey: 1,
      selectedIds: [],
      plottedVibe: plottableVibes[0],
      plottedScore: null,
      plottableVibes,
      deselectAll: true,
      plottableScores: [
        {key: 'overall', name: 'Vocal Delivery', unit: 'score'},
        {key: 'strengthOfOpening', name: 'Strength of Opening', unit: 'score'},
        {key: 'clarity', name: 'Clarity', unit: 'score'},
        {key: 'pace', name: 'Pace', unit: 'syllables per second'},
        {key: 'pauseToTalk', name: 'Pausing', unit: 'pause-to-talk ratio %'},
        // { key: 'upspeak', name: 'Upspeak', unit: 'count per minute' },
        {key: 'varietyVolume.inBand', name: 'Variety of Volume', unit: 'in-band %'},
        {key: 'varietyPace.value', name: 'Variety of Pace', unit: 'score'},
        {key: 'varietyPitch.value', name: 'Variety of Pitch', unit: 'score'}
      ],
      loading: true, // this is needed so selectedRecordings() supports both a default value and the "deselect all" link
    };
  },
  computed: {
    selectedRecordings() {
      if (!this.selectedIds.length && this.loading) {
        this.selectedIds = this._.map(this.recordings, recording => recording.recordingId);
      }
      return this._.filter(this.recordings, recording => this._.includes(this.selectedIds, recording.recordingId));
    }
  },
  watch: {
    selectedIds: 'notifyUserChartUpdated',
    plottedVibe: 'notifyUserChartUpdated',
    plottedScore: 'notifyUserChartUpdated',
  },
  methods: {
    basePageCreated() {
      // TODO: Dont query listGroups in BasePage for speakers
      const lookups = [
        this.$store.dispatch('listRecordings'),
        this.$store.dispatch('getOrg'),
      ];
      if (this.isGroupManager) {
        lookups.push(this.$store.dispatch('listGroupMembers'));
        this.activeGroups.forEach((g) => lookups.push(this.$store.dispatch('listGroupRecordings', {groupId: g.groupId})));
      }
      window.Promise.all(lookups).then(() => {
        this.recordings = (this.pageMode === 'speaker') ?
          this._.filter(this.$store.getters.singleSpeakerRecordings, recording => !!recording.results.scores) : [];

        this.selectedRecordings; // initialize while loading is still true

        this.plottedScore = this.plottableScores[0];
        this.loading = false;
      });
    },
    setUser(user) {
      let recordings = [];
      this.selectedIds = [];

      // Populate "recordings"
      this._.forEach(this.sharedRecordings, (group, groupId) => {
        for (let userId in group) {
          if (group[userId] && userId == user.id && groupId === user.groupId) {
            recordings = recordings.concat(group[userId]);
          }
        }
      });

      // Set up the recordings/selectedIds and reset form values
      this.recordings = this._.sortBy(
        this._.filter(recordings, recording => !!(recording.results && recording.results.scores && !recording.results.scores.isConversational)),
        r => -new Date(r.createdDate).getTime()
      );
      this.selectedIds = this._.map(this.recordings, recording => recording.recordingId);
      this.deselectAll = true;
      this.user = user;
    },
    notifyUserChartUpdated() {
      const score = (this.tab === 'plot vibes') ? this.plottedVibe : this.plottedScore;
      if (!score) return; // Exit when the page first loads
      this.$announcer.set(
        `The table has been updated to show ${score.name} for the selected recordings`
      );
    },
    loadTab(identifier, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      const link = this.$refs[identifier.replace(/ /g, '') + 'link'];
      link.blur();
      this.$nextTick(() => {
        const header = document.getElementById(identifier.replace(/ /g, '') + 'header');
        if (header) {
          header.focus();
        }
      });

      this.tab = identifier;
      this.$announcer.set(`The ${identifier} tab has been activated. The associated page content is now available below.`);
    },
    toggleSelected(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      if (this.deselectAll) {
        this.deselectAll = false;
        this.selectedIds = [];
      } else {
        this.deselectAll = true;
        this.selectedIds = this._.map(this.recordings, recording => recording.recordingId);
      }
    }
  }
};
</script>


